import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ResponsiveFlex, TextInput } from "@narmi/design_system";
import { ProgressButtons } from "cerulean";
import { cents, entities, modules, useFeature } from "byzantine";
import { type LimitsForDisplayType } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useLocalization } from "@fluent/react";
import ComboAccountSelector from "../../ComboAccountSelector";
import * as formAdapters from "../../../src/adapters";
import HeaderWithSteps from "../../HeaderWithSteps";
import { SudoProvider, useSudoContext } from "../../SudoContext";
import SudoDialog from "../../SudoDialog";
import LimitsDialog from "../LimitsDialog";
import { TRANSFERS_ROUTE, TRANSFER_DETAILS_ROUTE } from "./TransferRoutes";

export const TOTAL_STEPS = 3;

function AccountSelectSection({ limits }: { limits: LimitsForDisplayType }) {
  const institution = entities.institution.useInstitution();
  const { featureEnabled } = useFeature();
  const form = modules.transfers.TransferForm.useForm();
  const selectedSourceAccount = modules.transfers.useSelectedSourceAccount();
  const selectedDestinationAccount =
    modules.transfers.useSelectedDestinationAccount();
  const [searchParams] = useSearchParams();
  const { l10n } = useLocalization();

  const sourceAccounts = entities.accounts.useValidTransferSources(
    selectedDestinationAccount?.id,
    featureEnabled("internal_transfers")
  );
  const prepopulateFromAccount = searchParams.get("from_account_id");
  const prepopulateToAccount = searchParams.get("to_account_id");
  const prepopulateAmount = searchParams.get("amount");
  const basename = "funds";

  const destinationAccounts = entities.accounts.useValidTransferDestinations(
    !!institution?.ach_allows_push,
    selectedSourceAccount?.id,
    featureEnabled("internal_transfers"),
    featureEnabled("enable_loans_in_transfer_flow")
  );

  const hasPrepopulated = useRef(false);

  useEffect(() => {
    if (hasPrepopulated.current) return;

    // Wait until accounts have loaded before attempting to match
    if (
      prepopulateFromAccount &&
      !sourceAccounts.find((a) => a.id === prepopulateFromAccount)
    )
      return;
    if (
      prepopulateToAccount &&
      !destinationAccounts.find((a) => a.id === prepopulateToAccount)
    )
      return;

    hasPrepopulated.current = true;

    if (prepopulateFromAccount) {
      form.setFieldValue("fromAccount", prepopulateFromAccount, true);
    }
    if (prepopulateToAccount) {
      form.setFieldValue("toAccount", prepopulateToAccount, true);
    }
    if (prepopulateAmount) {
      const amountCents = parseInt(prepopulateAmount, 10);
      if (!Number.isNaN(amountCents) && amountCents > 0) {
        form.setFieldValue("amount", cents(amountCents), true);
      }
    }
  }, [
    prepopulateFromAccount,
    prepopulateToAccount,
    prepopulateAmount,
    sourceAccounts,
    destinationAccounts,
  ]);

  return (
    <>
      <ResponsiveFlex gapSize="s">
        <h4 className="fontSize--m nds-sans">
          {l10n.getString("header-transfer-details")}
        </h4>
        <div className="transfer-amount-container">
          <TextInput
            label="Amount"
            {...formAdapters.amountInput(form, "amount")}
          />
        </div>
        <div style={{ float: "left" }}>
          <LimitsDialog limitsForDisplay={limits} />
        </div>
        <h4 className="fontSize--m nds-sans padding--top--xs">
          {l10n.getString("header-select-accounts")}
        </h4>
        <SudoProvider basename={basename}>
          <div className="transfer-accounts-container padding--bottom--s">
            <ComboAccountSelector
              accounts={sourceAccounts}
              label={l10n.getString("label-from")}
              isDestination={false}
              {...formAdapters.select(form, "fromAccount")}
              basename={basename}
            />
          </div>
          <div className="transfer-accounts-container">
            <ComboAccountSelector
              accounts={destinationAccounts}
              label={l10n.getString("label-to")}
              isDestination={true}
              {...formAdapters.select(form, "toAccount")}
              basename={basename}
            />
          </div>
          <SudoDialog />
        </SudoProvider>
      </ResponsiveFlex>
    </>
  );
}

export default function AccountSelectScreen({
  limits,
}: {
  limits: LimitsForDisplayType;
}) {
  const navigate = useNavigate();
  const { l10n } = useLocalization();
  const { onSetInitialSteps } = useSudoContext();
  const { checkDestinationInstantEligibility } =
    modules.transfers.useCheckDestinationInstantEligibility();
  const { getInstantTransferFees } =
    modules.transfers.useGetInstantTransferFees();
  const destination = modules.transfers.useSelectedDestinationAccount();
  const balanceLimitsValid = modules.transfers.useBasicBalanceLimitCheck();
  const form = modules.transfers.TransferForm.useForm();

  const handleSubmit = useCallback(async () => {
    checkDestinationInstantEligibility();
    getInstantTransferFees();
    const { success } = await form.submitForm();
    if (success) {
      if (balanceLimitsValid) {
        navigate(`/${TRANSFERS_ROUTE}/${TRANSFER_DETAILS_ROUTE}`);
      } else {
        form.setFieldError(
          "amount",
          l10n.getString("transfers-error-nsf-limits")
        );
      }
    }
  }, [form.values, destination]);

  useEffect(() => {
    onSetInitialSteps(TOTAL_STEPS);
  }, [onSetInitialSteps]);

  return (
    <ResponsiveFlex gapSize="xl">
      <HeaderWithSteps
        headerText={l10n.getString("header-make-a-transfer")}
        step={1}
        totalSteps={TOTAL_STEPS}
      />

      <AccountSelectSection limits={limits} />

      <ProgressButtons
        backLabel={l10n.getString("button-cancel")}
        nextLabel={l10n.getString("button-next")}
        onBack={() => {
          navigate("/");
        }}
        onNext={handleSubmit}
      />
    </ResponsiveFlex>
  );
}
